import type { PreviewData } from '../../api/preview';
import type { GetStaticPaths, GetStaticPathsResult, GetStaticProps, NextPage } from 'next';

import dynamic from 'next/dynamic';
import { memo, useEffect } from 'react';
import isEqual from 'react-fast-compare';

import type { MasterLayout as MasterLayoutSchema } from '~/amplienceTypes/schemas/imported/master-layout-schema';
import { HomepageWrapper } from '~/components/homepage/HomepageWrapper';
import { MobileCategoryRenderer } from '~/components/homepage/MobileCategoryRenderer';
import { PpeBanner } from '~/components/homepage/PpeBanner';
import { ProfileToggleProvider } from '~/components/homepage/ProfileToggleContext';
import { MasterLayout } from '~/components/shared/core/layouts/MasterLayout';
import { HomepageMetaHead } from '~/components/shared/metaHead/HomepageMetaHead';
import { Placeholder } from '~/components/shared/placeholder/Placeholder';
import type { CommonPageProps, GenericRouteParams, InpageNavProps } from '~/types/props';
import { showSlotContent } from '~/utilities/amplience';
import { PageTypes } from '~/utilities/constants';
import {
  HOMEPAGE_LAYOUT_SLOT_DELIVERY_KEY,
  MASTER_LAYOUT_SLOT_DELIVERY_KEY,
  QUICK_VIEW_OVERLAY_DELIVERY_KEY,
} from '~/utilities/constants/amplienceDeliveryKeys';
import { NavActionContextProvider } from '~/utilities/context/dynamic/NavActionContext';
import { StaticContextProvider } from '~/utilities/context/static/StaticContext';
import { NavigationTypeParam } from '~/utilities/graphql/codegen';
import type { SiteType } from '~/utilities/graphql/codegen';
import { getPpeDeliveryKeys } from '~/utilities/helpers';
import { getAmplienceHubName } from '~/utilities/helpers/getAmplienceHubName';
import { amplienceLocaleWithFallback } from '~/utilities/parsers';
import { NOT_FOUND_REVALIDATE, PAGES_REVALIDATE_TIME } from '~/utilities/ssr';
import { getAmplienceContent } from '~/utilities/ssr/cacheAmplienceContent';
import { fetchNav } from '~/utilities/ssr/cacheGraphqlContent';
import { getHomepageDeliveryKey } from '~/utilities/ssr/getHomepageDeliveryKeys';
import { getLocalesForSiteType } from '~/utilities/ssr/staticPaths';
import { filterCommonAmplienceContents } from '~/utilities/ssr/staticProps';
import { getCommonStaticProps } from '~/utilities/ssr/staticProps/getCommonStaticProps';
import { getTopLevelNavigationDeliveryKeys } from '~/utilities/ssr/topLevelNavigation';

const ClearPreviewModeCookiesButton = dynamic(
  async () =>
    (await import('~/components/shared/cookie/ClearPreviewModeCookiesButton'))
      .ClearPreviewModeCookiesButton
);

type HomepageProps = CommonPageProps & InpageNavProps;

const Homepage: NextPage<HomepageProps> = ({
  genericLayoutSlotContent,
  masterLayoutSlotContent,
  leftNavigation,
  navLinks,
  inpageNavigation,
  configuration,
  socialMediaChannels,
  countryEn,
  quickViewContent,
  topLevelNavigationContent,
  isPreviewMode,
  ppeContent,
  pageType,
}: HomepageProps) => {
  useEffect(() => {
    if ('PerformanceObserver' in window) {
      new PerformanceObserver(entryList => {
        // eslint-disable-next-line no-restricted-syntax
        for (const entry of entryList.getEntries()) {
          const lcpElement = (entry as { element: Element } & PerformanceEntry).element;

          lcpElement?.setAttribute('loading', 'eager');
        }
      }).observe({ type: 'largest-contentful-paint', buffered: true });
    }
  }, []);

  if (!masterLayoutSlotContent) {
    return <Placeholder />;
  }

  const main = genericLayoutSlotContent?.slotContent ? (
    <ProfileToggleProvider>
      <HomepageWrapper>
        <MobileCategoryRenderer inpageNavigation={inpageNavigation} />
        <PpeBanner />
        {showSlotContent(genericLayoutSlotContent?.slotContent)}
      </HomepageWrapper>
    </ProfileToggleProvider>
  ) : (
    <Placeholder />
  );

  const metadataContent = genericLayoutSlotContent?.metaData;
  const uspListContent = genericLayoutSlotContent?.uspList;

  return (
    <div className="page--landingPage has-mouse">
      <StaticContextProvider
        pageType={pageType}
        configuration={configuration || {}}
        leftNavigation={leftNavigation || {}}
        navLinks={navLinks}
        socialMediaChannels={socialMediaChannels || []}
        quickViewContent={quickViewContent}
        countryEn={countryEn || {}}
        accessibilityLabels={masterLayoutSlotContent?.slotContent?.accessibilityLabels?.content}
        formErrorLabels={masterLayoutSlotContent?.slotContent?.formErrorLabels?.content}
        notifications={masterLayoutSlotContent?.slotContent?.notifications?.content}
        ppeContent={ppeContent}
        topLevelNavigationContent={topLevelNavigationContent}
      >
        <NavActionContextProvider
          defaultValue={genericLayoutSlotContent?.pageProperties?.sidenavDefaultOpen}
        >
          <HomepageMetaHead metadataContent={metadataContent} />
          {isPreviewMode && <ClearPreviewModeCookiesButton />}
          <MasterLayout
            pageProperties={genericLayoutSlotContent?.pageProperties}
            uspListContent={uspListContent}
            pageType={pageType}
            {...(masterLayoutSlotContent?.slotContent as MasterLayoutSchema)}
          >
            {main}
          </MasterLayout>
        </NavActionContextProvider>
      </StaticContextProvider>
    </div>
  );
};

export default memo(Homepage, isEqual);

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const sitesToBuild = (process.env.SITE_TYPES_TO_BUILD?.split(',') ?? []) as Array<SiteType>;

  if (!locales) {
    throw new Error('locales needed');
  }

  const paths: GetStaticPathsResult['paths'] = (
    await Promise.all(
      sitesToBuild.map(async siteType => {
        const indexPaths: GetStaticPathsResult['paths'] = [];
        const intersectLocales = (await getLocalesForSiteType(siteType, locales || [])) || [];

        intersectLocales.forEach((locale: string) => {
          indexPaths.push({ params: { siteType }, locale });
        });

        return indexPaths;
      })
    )
  ).flat();

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({
  locale,
  preview,
  previewData,
  params: { ...params },
}) => {
  const logger = (await import('~/utilities/logger')).default;

  const { siteType } = params as GenericRouteParams;
  const pageType = PageTypes.HOME;

  const amplienceHubName = getAmplienceHubName(siteType);

  if (!locale || locale === 'default') {
    logger.debug('[homepage] Locale not found');

    return NOT_FOUND_REVALIDATE;
  }

  try {
    const commonProps = await getCommonStaticProps({
      siteType,
      locale,
      pageType,
    });

    if (!commonProps.configuration) {
      logger.error('Configuration data is empty from backend.');

      return NOT_FOUND_REVALIDATE;
    }

    const inpageNavigation = await fetchNav(siteType, locale, NavigationTypeParam.InpageNavigation);

    const homepageLayoutSlotDeliveryKey =
      commonProps.configuration.homepageLayoutSlotDeliveryKey || HOMEPAGE_LAYOUT_SLOT_DELIVERY_KEY;
    const masterLayoutSlotDeliveryKey =
      commonProps.configuration.masterLayoutSlotDeliveryKey || MASTER_LAYOUT_SLOT_DELIVERY_KEY;
    const quickViewDeliveryKey =
      commonProps.configuration.quickViewOverlayDeliveryKey || QUICK_VIEW_OVERLAY_DELIVERY_KEY;

    const { repoId } = commonProps.configuration;

    const amplienceContents = await getAmplienceContent(
      [
        ...getHomepageDeliveryKey(homepageLayoutSlotDeliveryKey, siteType, repoId),
        { key: masterLayoutSlotDeliveryKey },
        { key: quickViewDeliveryKey },
        ...getTopLevelNavigationDeliveryKeys(commonProps.configuration),
        ...getPpeDeliveryKeys(commonProps.configuration),
      ],
      amplienceLocaleWithFallback(locale),
      amplienceHubName,
      previewData as PreviewData
    );

    return {
      props: {
        ...commonProps,
        ...filterCommonAmplienceContents(amplienceContents, pageType),
        inpageNavigation,
        isPreviewMode: !!preview,
        siteType,
        pageType,
      },
      revalidate: PAGES_REVALIDATE_TIME[pageType],
    };
  } catch (error) {
    logger.error(`Error caught for homepage: ${(error as Error).message}`);

    return NOT_FOUND_REVALIDATE;
  }
};
